/**
 * Extension Authentication Sync Utility
 * Syncs webapp authentication with Chrome Extension
 */

/* global chrome */

// Extension ID will be retrieved dynamically or set via environment variable
// User needs to set this in .env as REACT_APP_EXTENSION_ID after installing the extension
const EXTENSION_ID = process.env.REACT_APP_EXTENSION_ID;

console.log('[ExtensionSync] DEBUG - Extension ID:', EXTENSION_ID);
console.log('[ExtensionSync] DEBUG - All env vars:', process.env);

/**
 * Sync authentication with Chrome Extension
 * Called after user logs into webapp
 * @param {Object} user - Firebase User object
 */
export async function syncAuthWithExtension(user) {
  // Skip if not in browser or extension ID not configured
  if (typeof window === 'undefined' || typeof chrome === 'undefined' || !chrome.runtime) {
    console.log('[ExtensionSync] Not in Chrome or extension not available');
    return;
  }

  if (!EXTENSION_ID) {
    console.warn('[ExtensionSync] EXTENSION_ID not configured. Set REACT_APP_EXTENSION_ID in .env');
    return;
  }

  console.log('[ExtensionSync] Using Extension ID:', EXTENSION_ID);

  try {
    console.log('[ExtensionSync] Starting auth sync with extension...');

    // Get Firebase ID token
    const idToken = await user.getIdToken();

    // Call Cloud Function to get custom token
    const response = await fetch(
      'https://us-central1-ezypipe.cloudfunctions.net/generateCustomToken',
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success || !data.customToken) {
      throw new Error('Failed to get custom token');
    }

    console.log('[ExtensionSync] Custom token received, sending to extension...');

    // Send custom token to extension
    chrome.runtime.sendMessage(
      EXTENSION_ID,
      {
        type: 'SIGN_IN_WITH_TOKEN',
        data: { token: data.customToken }
      },
      (response) => {
        if (chrome.runtime.lastError) {
          console.error('[ExtensionSync] Error communicating with extension:', chrome.runtime.lastError);
          return;
        }

        if (response && response.success) {
          console.log('[ExtensionSync] ✓ Extension authenticated successfully');
        } else {
          console.error('[ExtensionSync] Extension authentication failed:', response?.error);
        }
      }
    );
  } catch (error) {
    console.error('[ExtensionSync] Error syncing auth with extension:', error);
  }
}

/**
 * Sync logout with Chrome Extension
 * Called when user logs out from webapp
 */
export async function syncLogoutWithExtension() {
  // Skip if not in browser or extension ID not configured
  if (typeof window === 'undefined' || typeof chrome === 'undefined' || !chrome.runtime) {
    console.log('[ExtensionSync] Not in Chrome or extension not available');
    return;
  }

  if (!EXTENSION_ID) {
    console.warn('[ExtensionSync] EXTENSION_ID not configured. Set REACT_APP_EXTENSION_ID in .env');
    return;
  }

  try {
    console.log('[ExtensionSync] Syncing logout with extension...');

    // Send logout message to extension
    chrome.runtime.sendMessage(
      EXTENSION_ID,
      { type: 'SIGN_OUT' },
      (response) => {
        if (chrome.runtime.lastError) {
          console.error('[ExtensionSync] Error communicating with extension:', chrome.runtime.lastError);
          return;
        }

        if (response && response.success) {
          console.log('[ExtensionSync] ✓ Extension logged out successfully');
        } else {
          console.error('[ExtensionSync] Extension logout failed:', response?.error);
        }
      }
    );
  } catch (error) {
    console.error('[ExtensionSync] Error syncing logout with extension:', error);
  }
}

/**
 * Check if Chrome Extension is installed and responsive
 * @returns {Promise<boolean>}
 */
export async function isExtensionInstalled() {
  if (typeof window === 'undefined' || typeof chrome === 'undefined' || !chrome.runtime) {
    return false;
  }

  if (!EXTENSION_ID) {
    return false;
  }

  return new Promise((resolve) => {
    try {
      chrome.runtime.sendMessage(
        EXTENSION_ID,
        { type: 'PING' },
        (response) => {
          if (chrome.runtime.lastError) {
            resolve(false);
          } else {
            resolve(true);
          }
        }
      );
    } catch (error) {
      resolve(false);
    }
  });
}
/**
 * Sync settings with Chrome Extension
 * Called after user updates settings in webapp
 */
export async function syncSettingsWithExtension() {
  // Skip if not in browser or extension ID not configured
  if (typeof window === 'undefined' || typeof chrome === 'undefined' || !chrome.runtime) {
    return;
  }

  if (!EXTENSION_ID) {
    return;
  }

  try {
    console.log('[ExtensionSync] Syncing settings with extension...');

    // Send generic update message to extension
    chrome.runtime.sendMessage(
      EXTENSION_ID,
      { type: 'SYNC_SETTINGS' },
      (response) => {
        if (chrome.runtime.lastError) {
          console.error('[ExtensionSync] Error communicating with extension:', chrome.runtime.lastError);
          return;
        }
        console.log('[ExtensionSync] ✓ Extension settings sync triggered');
      }
    );
  } catch (error) {
    console.error('[ExtensionSync] Error syncing settings with extension:', error);
  }
}

/**
 * Sync session token with Chrome Extension
 * Called after webapp creates session token on login
 * @param {string} sessionToken - Session token
 * @param {number} expiresAt - Token expiration timestamp
 * @param {Object} user - Firebase User object (optional, for user info)
 */
export async function syncSessionTokenWithExtension(sessionToken, expiresAt, user = null) {
  // Skip if not in browser or extension ID not configured
  if (typeof window === 'undefined' || typeof chrome === 'undefined' || !chrome.runtime) {
    console.log('[ExtensionSync] Not in Chrome or extension not available');
    return;
  }

  if (!EXTENSION_ID) {
    console.warn('[ExtensionSync] EXTENSION_ID not configured. Set REACT_APP_EXTENSION_ID in .env');
    return;
  }

  try {
    console.log('[ExtensionSync] Syncing session token with extension, expires:', new Date(expiresAt).toISOString());

    // Send session token AND user info to extension
    chrome.runtime.sendMessage(
      EXTENSION_ID,
      {
        type: 'SYNC_SESSION_TOKEN',
        data: {
          sessionToken,
          expiresAt,
          userId: user?.uid || null,
          userEmail: user?.email || null,
        }
      },
      (response) => {
        if (chrome.runtime.lastError) {
          console.error('[ExtensionSync] Error communicating with extension:', chrome.runtime.lastError);
          return;
        }

        if (response && response.success) {
          console.log('[ExtensionSync] ✓ Session token synced with extension');
        } else {
          console.error('[ExtensionSync] Session token sync failed:', response?.error);
        }
      }
    );
  } catch (error) {
    console.error('[ExtensionSync] Error syncing session token with extension:', error);
  }
}
